import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SidebarService {
    // Map to store multiple sidebars by ID
    private sidebars = new Map<string, BehaviorSubject<boolean>>();
    private activeSidebarId: string | null = null;

    constructor() { }

    /**
     * Get sidebar state from localStorage
     */
    getSidebarStateFromStorage(sidebarId: string): boolean {
        const stored = localStorage.getItem(sidebarId);
        return stored !== null ? JSON.parse(stored) : true;
    }

    /**
     * Save sidebar state to localStorage
     */
    saveSidebarStateToStorage(sidebarId: string, visible: boolean): void {
        localStorage.setItem(sidebarId, JSON.stringify(visible));
    }

    /**
     * Register a sidebar with the given ID
     */
    registerSidebar(sidebarId: string): void {
        if (!this.sidebars.has(sidebarId)) {
            const initialState = this.getSidebarStateFromStorage(sidebarId);
            this.sidebars.set(sidebarId, new BehaviorSubject<boolean>(initialState));
        }
        // Set as active sidebar if no active sidebar is set
        if (!this.activeSidebarId) {
            this.activeSidebarId = sidebarId;
        }
    }

    /**
     * Set the active sidebar by ID (for toggling from top-bar)
     */
    setActiveSidebar(sidebarId: string): void {
        this.activeSidebarId = sidebarId;
    }

    /**
     * Toggle the currently active sidebar and persist the new state to localStorage.
     * This represents an intentional user action, so the choice survives a refresh.
     */
    toggleSidebar(sidebarId?: string): void {
        const targetId = sidebarId || this.activeSidebarId;
        if (targetId && this.sidebars.has(targetId)) {
            const subject = this.sidebars.get(targetId)!;
            const newValue = !subject.value;
            subject.next(newValue);
            this.saveSidebarStateToStorage(targetId, newValue);
        }
    }

    /**
     * Set sidebar visibility by ID.
     * @param persist - when false, updates the in-memory state only (no localStorage write).
     *                  Use this for transient overrides like auto-collapsing on mobile.
     */
    setSidebarVisible(sidebarId: string, visible: boolean, persist = true): void {
        if (this.sidebars.has(sidebarId)) {
            this.sidebars.get(sidebarId)!.next(visible);
            if (persist) {
                this.saveSidebarStateToStorage(sidebarId, visible);
            }
        }
    }

    /**
     * Get sidebar visibility observable by ID
     */
    getSidebarVisible$(sidebarId: string): Observable<boolean> {
        if (!this.sidebars.has(sidebarId)) {
            this.registerSidebar(sidebarId);
        }
        return this.sidebars.get(sidebarId)!.asObservable();
    }

    /**
     * Check if sidebar is visible by ID
     */
    isSidebarVisible(sidebarId: string): boolean {
        return this.sidebars.get(sidebarId)?.value ?? true;
    }

    /**
     * Get the active sidebar ID
     */
    getActiveSidebarId(): string | null {
        return this.activeSidebarId;
    }
}

